import { useEffect, useState } from 'react';
import { esErrorDeRed, mensajeDeError, useSinConexion } from '@/core/red/conectividad';
import { useBundleOpcional } from '@/core/offline/BundleProvider';
import { createEstablishment, listEstablishments, type Establishment } from './clients';

interface Props {
  organizationId: string;
  clientId: string;
  value: Establishment | null;
  onChange: (establishment: Establishment | null) => void;
}

/**
 * Selector de establecimiento con alta en línea.
 *
 * Igual que `ClientPicker`: crear un campo NO debe hacer perder el trabajo que
 * se está cargando. Por eso el alta ocurre acá dentro y devuelve el
 * establecimiento ya seleccionado.
 *
 * Sin señal los campos salen de la copia operativa. Es el renglón siguiente al
 * de elegir el cliente, y sin él la secuencia se corta ahí: no alcanza con
 * encontrar al cliente si después la lista de sus campos aparece vacía y con un
 * error al lado. Lo que SÍ necesita servidor es crear uno nuevo —un campo
 * inventado en el teléfono no tendría identificador contra el que guardar el
 * trabajo—, así que ese botón se apaga y se dice por qué.
 */
export function EstablishmentPicker({ organizationId, clientId, value, onChange }: Props) {
  const [items, setItems] = useState<Establishment[]>([]);
  const [nuevo, setNuevo] = useState('');
  const [creando, setCreando] = useState(false);
  const [abrirAlta, setAbrirAlta] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);
  const sinServidor = useSinConexion();
  const { bundle } = useBundleOpcional() ?? { bundle: null };

  useEffect(() => {
    let cancelado = false;

    const mostrar = (lista: Establishment[]) => {
      const activos = lista.filter((e) => e.is_active);
      setItems(activos);
      // Si el cliente tiene un solo campo, no tiene sentido preguntarlo.
      if (!value && activos.length === 1) onChange(activos[0] ?? null);
    };
    const local = () => mostrar(bundle?.establecimientos[clientId] ?? []);

    void (async () => {
      if (sinServidor) {
        if (!cancelado) local();
        return;
      }
      try {
        const lista = await listEstablishments(clientId);
        if (cancelado) return;
        setError(null);
        mostrar(lista);
      } catch (e) {
        if (cancelado) return;
        // Se cortó la señal después de elegir el cliente: los campos guardados
        // sirven igual, y son los mismos que se venían usando.
        if (esErrorDeRed(e)) local();
        else setError(mensajeDeError(e));
      }
    })();
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, recarga, sinServidor, bundle]);

  const crear = async () => {
    if (nuevo.trim() === '') return;
    setCreando(true);
    setError(null);
    try {
      const creado = await createEstablishment({ organizationId, clientId, name: nuevo });
      setNuevo('');
      setAbrirAlta(false);
      setRecarga((n) => n + 1);
      onChange(creado);
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setCreando(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm" htmlFor="establishment-picker">
        Establecimiento
      </label>

      <div className="flex gap-2">
        <select
          id="establishment-picker"
          value={value?.id ?? ''}
          onChange={(e) => onChange(items.find((i) => i.id === e.target.value) ?? null)}
          className="min-h-11 flex-1 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-2"
          data-testid="establishment-select"
        >
          <option value="">Sin especificar</option>
          {items.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setAbrirAlta((v) => !v)}
          disabled={sinServidor}
          className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-sm disabled:opacity-50"
          data-testid="establishment-new-toggle"
        >
          + Campo
        </button>
      </div>

      {sinServidor && (
        <p className="text-xs text-[var(--tinta-suave)]" data-testid="establishment-offline">
          {items.length > 0
            ? 'Sin conexión · mostrando los campos guardados en este teléfono'
            : 'Sin conexión y este cliente no tiene campos guardados en el teléfono. Se puede cargar el trabajo sin especificar el campo.'}
        </p>
      )}

      {abrirAlta && (
        <div className="mt-1 flex gap-2" data-testid="establishment-new">
          <input
            value={nuevo}
            onChange={(e) => setNuevo(e.target.value)}
            placeholder="Nombre del campo"
            className="min-h-11 flex-1 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3"
            data-testid="establishment-new-name"
          />
          <button
            type="button"
            onClick={() => void crear()}
            disabled={creando}
            className="min-h-11 rounded-[var(--radius-control)] bg-[var(--accion)] px-4 text-sm text-[var(--sobre-accion)] disabled:opacity-50"
            data-testid="establishment-new-save"
          >
            {creando ? 'Creando…' : 'Crear'}
          </button>
        </div>
      )}

      {error && <p className="text-sm text-[var(--peligro)]">{error}</p>}
    </div>
  );
}
